<template>
  <st-box class="mt-24">
    <div class="mx-auto flex justify-center flex-col items-center gap-20">
      <img
        src="@/assets/images/build/28-1-2-img.svg"
        alt=""
        class="block h-188 w-272 object-contain"
      />
      <h3 class="break-keep text-center text-3xl font-bold leading-2xl text-on-surface-elevation-1">
        {{ $t('studio.prj_prod_mngmt.rating_build_review.self_review.guide1') }}
      </h3>
      <p class="break-keep text-center font-regular text-md leading-lg text-on-surface-elevation-3">
        <safe-html :html="$t('studio.prj_prod_mngmt.rating_build_review.self_review.guide1_1')" />
      </p>
    </div>
    <div class="grid gap-24 grid-cols-3 mt-40">
      <rating-guide-item
        :status="businessGuide.status"
        :title="'studio.prj_prod_mngmt.rating_build_review.self_review.bims_bank_info'"
        :description="businessGuide.description"
        :buttonText="businessGuide.buttonText"
        @click="navigateToBusinessBank"
      />
      <rating-guide-item
        :status="buildGuide.status"
        :title="'studio.prj_prod_mngmt.rating_build_review.self_review.build_upl'"
        :description="buildGuide.description"
        :buttonText="buildGuide.buttonText"
        @click="navigateToBuildManagement"
      />
      <rating-guide-item
        :title="'studio.prj_prod_mngmt.rating_build_review.self_review.game_content'"
        :description="'studio.prj_prod_mngmt.rating_build_review.self_review.game_content_guide'"
      />
    </div>
  </st-box>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { fetchProductBuildUploadCompletedApi } from '@/apis/build.api';
import { fetchCompanyStatusApi } from '@/apis/business-bank-info.api';
import SafeHtml from '@/components/common/safe-html.vue';
import StBox from '@/components/common/st-box.vue';
import RatingGuideItem from '@/components/rating/rating-guide-item.vue';
import { BUSINESS_BANK_PAGE_URL, PRODUCT_BUILD_PAGE_URL } from '@/constants/url.const';
import { BusinessReviewStatus } from '@/enums/business.enum';
import { RatingGuideItemType } from '@/enums/rating.enum';
import useProductStore from '@/stores/product.store';
import { useUserStore } from '@/stores/user.store';
import { redirectTo } from '@/utils/common.util';

const userStore = useUserStore();
const { selectedGroupId } = storeToRefs(userStore);

const productStore = useProductStore();
const { product } = storeToRefs(productStore);
const gameId = product.value?.gameId || '';

const navigateToBusinessBank = () => {
  redirectTo(BUSINESS_BANK_PAGE_URL);
};

const navigateToBuildManagement = () => {
  redirectTo(PRODUCT_BUILD_PAGE_URL);
};

const businessGuide = ref<{
  description: string;
  buttonText: string;
  status: RatingGuideItemType | null;
}>({
  description: 'studio.prj_prod_mngmt.rating_build_review.self_review.bims_bank_info_guide',
  buttonText: 'studio.prj_prod_mngmt.rating_build_review.self_review.bims_bank_info_pg_dir_btn',
  status: null
});

const buildGuide = ref<{
  description: string;
  buttonText: string;
  status: RatingGuideItemType | null;
}>({
  description: 'studio.prj_prod_mngmt.rating_build_review.self_review.build_upl_guide',
  buttonText: 'studio.prj_prod_mngmt.rating_build_review.self_review.build_mgmt_pg_dir_btn',
  status: null
});

const getBusinessInfo = async () => {
  const companyStatus = await fetchCompanyStatusApi(selectedGroupId.value);
  if (companyStatus) {
    if (companyStatus === BusinessReviewStatus.Approve) {
      businessGuide.value = {
        description: 'studio.rating_build_review.biz_info_register_completed_guide',
        buttonText: '',
        status: RatingGuideItemType.Approve
      };
    } else if (companyStatus === BusinessReviewStatus.Reject) {
      businessGuide.value = {
        description: 'studio.rating_build_review.biz_info_rejected_guide',
        buttonText: 'studio.rating_build_review.view_reject_reason_btn',
        status: RatingGuideItemType.Reject
      };
    } else if (companyStatus === BusinessReviewStatus.Request) {
      businessGuide.value = {
        description: 'studio.rating_build_review.biz_info_in_review_guide',
        buttonText: 'studio.rating_build_review.view_review_stt_btn',
        status: RatingGuideItemType.Request
      };
    }
  }
};

const getBuildList = async () => {
  const buildRes = await fetchProductBuildUploadCompletedApi({
    gameId
  });
  if (buildRes) {
    if (buildRes.length > 0) {
      buildGuide.value = {
        description: 'studio.rating_build_review.build_register_completed_guide',
        buttonText: '',
        status: RatingGuideItemType.Inputted
      };
    }
  }
};

const init = () => {
  getBusinessInfo();
  getBuildList();
};

init();
</script>
